import React, { createContext, useState, useEffect, useContext } from "react";
import {
  logIn,
  signUp,
  logOut,
  getCurrentUser,
} from "../services/parseService";

interface AuthContextProps {
  currentUser: any | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextProps>({
  currentUser: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
  error: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for current user on load
    const checkCurrentUser = async () => {
      try {
        setIsLoading(true);
        const user = await getCurrentUser();
        setCurrentUser(user);
      } catch (err) {
        console.error("Error checking current user:", err);
        // Clear user state if there's an error
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkCurrentUser();

    // Listen for session expiration events
    const handleSessionExpired = () => {
      console.log("Session expired event received, logging out user");
      setCurrentUser(null);
      setError("Your session has expired. Please log in again.");
    };

    // Add event listener for session expiration
    if (typeof window !== 'undefined') {
      window.addEventListener('sessionExpired', handleSessionExpired);
    }

    // Cleanup event listener
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('sessionExpired', handleSessionExpired);
      }
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const user = await logIn(username, password);
      setCurrentUser(user);
    } catch (error: any) {
      setError(error.message || "Failed to login");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (username: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const user = await signUp(username, email, password);
      setCurrentUser(user);
    } catch (error: any) {
      setError(error.message || "Failed to signup");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await logOut();
      setCurrentUser(null);
    } catch (error: any) {
      setError(error.message || "Failed to logout");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    currentUser,
    isLoading,
    isAuthenticated: !!currentUser,
    login,
    signup,
    logout,
    error,
  };

  return (
    <AuthContext.Provider value={value} data-oid="7v_cwjd">
      {children}
    </AuthContext.Provider>
  );
};
